import axios, { type AxiosInstance, isAxiosError } from 'axios';
import { trackEvent } from '@/analytics';
import { showErrorToast } from '@/components/Toast';
import { AnalyticsEvents } from '@/constants/analytics';
import { clearLastSeenNumber } from '@/hooks/useLastSeenNumber/lastSeenNumberStorage';
import { useAuthTokenStore } from '@/store/authTokenStore';
import { environment } from '@/utils/env.utils';

/**
 * Given an errorId, it returns the translation key that this errorId corresponds to
 */
const getI18nErrorMessageKey = (errorId: number) => {
  if (errorId === 0) {
    return 'bankId';
  } else if (errorId === 1) {
    return 'loginTookLong';
    // errorId 3, 17 was removed from this array because we should handle it per case and not globally
  } else if ([2, 4, 5, 6, 11, 12, 13, 14, 15, 16, 18].includes(errorId)) {
    return 'somethingWentWrong';
    // errorId 9 was removed from this array because we should handle it per case and not globally
  } else if ([7, 8, 10].includes(errorId)) {
    return 'inactivity';
  }
  return undefined;
};

const createAxiosInstance = (apiUrl: string): AxiosInstance => {
  const instance = axios.create({
    baseURL: apiUrl,
    headers: {
      Accept: 'application/json',
    },
  });

  instance.interceptors.request.use(config => {
    const token = useAuthTokenStore.getState().authToken;
    if (token && config.headers) {
      config.headers.Authorization = token;
    }

    return {
      ...config,
    };
  });

  instance.interceptors.response.use(
    response => response,
    error => {
      if (isAxiosError(error) && error.response) {
        const errorId = error.response.data.error?.errorId as number;
        const status = error.response.status;
        const url = error.config?.url;
        const errorKey = getI18nErrorMessageKey(errorId);
        if (errorKey) {
          trackEvent(AnalyticsEvents.AXIOS_GLOBAL_ERROR_TOAST, {
            status,
            url,
            errorId,
          });
          showErrorToast(`ApiErrors:${errorKey}`);
        }

        if (status === 401 || status === 403) {
          // TODO: Remove once we've confirmed global auth-token clearing on 401/403 is no longer needed
          trackEvent(AnalyticsEvents.AXIOS_GLOBAL_AUTH_TOKEN_REMOVAL, {
            status,
            url,
            errorId,
          });
          useAuthTokenStore.getState().removeAuthToken();
          clearLastSeenNumber();
        }
      }

      return Promise.reject(error);
    },
  );

  return instance;
};

const axiosInstance = createAxiosInstance(`${environment.apiRoot}${environment.apiRootSuffix}`);

export const axiosInstanceWithoutSuffix = createAxiosInstance(environment.apiRoot);

export default axiosInstance;
